// What makes each real map its own place: the colour of its sea and land, the cartouche it is titled in, and the
// towns, peaks, seas and landmarks drawn on it. Every feature is [kind, label, lon, lat] at its real position;
// `geo` turns lon/lat into the 0..1 box of src/sim/places.js ([k, x0, y0, size]: u = (lon*k - x0)/size,
// v = (-lat - y0)/size), worked out from the same Natural Earth outline by scripts/place_projection.py.
// Kinds: town (a dot and its name), sea (wide italic lettering on the water), area (italic lettering on the land),
// and the drawn ones (peak, volcano, lighthouse, ...: see GLYPHS in mapart.js), each with an optional label.
// frame: the cartouche's border; title: the place's own name for itself, under the map name.
export const PLACE_ART = {
  'Great Britain': {
    geo: [0.580319, -3.6191, -58.67707, 8.71794], sea: '#9cb8b9', paper: '#f1e8d2', waterLine: '#2c5560', accent: '#7a2433',
    frame: 'rule', title: 'Britannia', serif: true, motif: 'galleon',
    feats: [
      ['town', 'London', -0.128, 51.507], ['town', 'Edinburgh', -3.188, 55.953], ['town', 'Cardiff', -3.179, 51.481],
      ['town', 'Manchester', -2.244, 53.48], ['town', 'Glasgow', -4.251, 55.861], ['town', 'Inverness', -4.224, 57.478],
      ['peak', 'Ben Nevis', -5.004, 56.797], ['peak', 'Yr Wyddfa', -4.076, 53.068], ['stones', 'Stonehenge', -1.826, 51.179],
      ['castle', 'Caernarfon', -4.277, 53.139], ['lighthouse', "Land's End", -5.714, 50.066],
      ['sea', 'North Sea', 2.4, 56.4], ['sea', 'Irish Sea', -4.9, 53.9], ['sea', 'English Channel', -2.4, 50.25], ['sea', 'Atlantic Ocean', -8.2, 57.4],
    ],
  },
  Ireland: {
    geo: [0.5969, -6.25442, -55.38638, 3.94067], sea: '#93b6ae', paper: '#ede9cf', waterLine: '#24584c', accent: '#2f6b3a',
    frame: 'knot', title: 'Éire', serif: true, motif: 'currach',
    feats: [
      ['town', 'Dublin', -6.26, 53.35], ['town', 'Cork', -8.47, 51.898], ['town', 'Galway', -9.049, 53.271],
      ['town', 'Belfast', -5.93, 54.597], ['town', 'Limerick', -8.63, 52.664], ['town', 'Sligo', -8.476, 54.277],
      ['peak', 'Carrauntoohil', -9.742, 51.999], ['tower', 'Glendalough', -6.327, 53.011], ['cliffs', 'Cliffs of Moher', -9.426, 52.972],
      ['mound', 'Newgrange', -6.475, 53.695],
      ['sea', 'Atlantic Ocean', -10.6, 53.6], ['sea', 'Irish Sea', -5.3, 53.4], ['sea', 'Celtic Sea', -7.6, 51.3],
    ],
  },
  Iceland: {
    geo: [0.41894, -10.28075, -66.53661, 4.62383], sea: '#a2b5bf', paper: '#eceae2', waterLine: '#33505e', accent: '#2f4f7a',
    frame: 'basalt', title: 'Ísland', serif: false, motif: 'whale',
    feats: [
      ['town', 'Reykjavík', -21.895, 64.135], ['town', 'Akureyri', -18.09, 65.683], ['town', 'Ísafjörður', -23.124, 66.075],
      ['town', 'Egilsstaðir', -14.395, 65.265], ['town', 'Vík', -19.006, 63.419], ['town', 'Höfn', -15.209, 64.254],
      ['volcano', 'Hekla', -19.666, 63.983], ['geyser', 'Geysir', -20.302, 64.313], ['ice', 'Vatnajökull', -16.8, 64.4],
      ['sea', 'Greenland Sea', -18.8, 67.15], ['sea', 'Denmark Strait', -25.6, 66.4], ['sea', 'North Atlantic', -20.4, 63.0], ['sea', 'Norwegian Sea', -12.9, 66.3],
    ],
  },
  Sicily: {
    geo: [0.791562, 9.83675, -38.30264, 2.55248], sea: '#94b4c4', paper: '#f3e4c4', waterLine: '#284f6b', accent: '#b8462e',
    frame: 'tiles', title: 'Sicilia', serif: true, motif: 'galley',
    feats: [
      ['town', 'Palermo', 13.361, 38.116], ['town', 'Catania', 15.087, 37.502], ['town', 'Messina', 15.554, 38.193],
      ['town', 'Siracusa', 15.287, 37.075], ['town', 'Trapani', 12.513, 38.018], ['town', 'Agrigento', 13.577, 37.311],
      ['volcano', 'Etna', 14.995, 37.751], ['temple', 'Valle dei Templi', 13.585, 37.29],
      ['sea', 'Tyrrhenian Sea', 14.1, 38.55], ['sea', 'Ionian Sea', 15.7, 36.9], ['sea', 'Strait of Sicily', 12.6, 37.0],
    ],
  },
  Sardinia: {
    geo: [0.764329, 6.2169, -41.26264, 2.39832], sea: '#8fb7c2', paper: '#f2e5c8', waterLine: '#245468', accent: '#9a2f2a',
    frame: 'tiles', title: 'Sardigna', serif: true, motif: 'galley',
    feats: [
      ['town', 'Cagliari', 9.11, 39.223], ['town', 'Sassari', 8.56, 40.727], ['town', 'Olbia', 9.5, 40.923],
      ['town', 'Nuoro', 9.33, 40.321], ['town', 'Oristano', 8.592, 39.903], ['town', 'Alghero', 8.32, 40.558],
      ['nuraghe', 'Su Nuraxi', 8.991, 39.706], ['peak', 'Punta La Marmora', 9.328, 39.988],
      ['sea', 'Tyrrhenian Sea', 10.2, 40.0], ['sea', 'Sea of Sardinia', 7.4, 40.1], ['sea', 'Bocche di Bonifacio', 9.2, 41.36],
    ],
  },
  Corsica: {
    geo: [0.742049, 6.341, -43.0174, 1.65149], sea: '#8fb3bf', paper: '#efe5ca', waterLine: '#26505f', accent: '#2a2a2a',
    frame: 'rule', title: 'Corsica · Corse', serif: true, motif: 'galley',
    feats: [
      ['town', 'Ajaccio', 8.738, 41.919], ['town', 'Bastia', 9.45, 42.697], ['town', 'Corte', 9.149, 42.306],
      ['town', 'Porto-Vecchio', 9.279, 41.591], ['town', 'Calvi', 8.757, 42.567], ['town', 'Bonifacio', 9.159, 41.387],
      ['peak', 'Monte Cinto', 8.945, 42.38], ['genoese', 'Tour de la Parata', 8.611, 41.908],
      ['sea', 'Ligurian Sea', 9.0, 43.25], ['sea', 'Tyrrhenian Sea', 9.95, 42.1], ['sea', 'Mediterranean', 8.15, 41.65],
    ],
  },
  Crete: {
    geo: [0.816261, 19.19702, -35.68696, 2.2879], sea: '#8eb2c6', paper: '#f3e7cc', waterLine: '#234d6d', accent: '#1f5a8a',
    frame: 'meander', title: 'Κρήτη', serif: true, motif: 'galley',
    feats: [
      ['town', 'Heraklion', 25.144, 35.339], ['town', 'Chania', 24.018, 35.514], ['town', 'Rethymno', 24.473, 35.366],
      ['town', 'Agios Nikolaos', 25.716, 35.19], ['town', 'Ierapetra', 25.74, 35.01], ['town', 'Sitia', 26.1, 35.207],
      ['peak', 'Psiloritis', 24.769, 35.227], ['horns', 'Knossos', 25.163, 35.298], ['area', 'Samaria Gorge', 23.96, 35.27],
      ['sea', 'Sea of Crete', 25.0, 35.75], ['sea', 'Libyan Sea', 24.6, 34.72],
    ],
  },
  Cyprus: {
    geo: [0.818539, 26.41567, -35.69196, 1.89967], sea: '#93b5c3', paper: '#f2e3c6', waterLine: '#2a5266', accent: '#b0662f',
    frame: 'meander', title: 'Κύπρος · Kıbrıs', serif: true, motif: 'galley',
    feats: [
      ['town', 'Nicosia', 33.365, 35.175], ['town', 'Limassol', 33.044, 34.684], ['town', 'Larnaca', 33.636, 34.917],
      ['town', 'Paphos', 32.416, 34.772], ['town', 'Famagusta', 33.94, 35.125], ['town', 'Kyrenia', 33.317, 35.34],
      ['peak', 'Olympus', 32.864, 34.936], ['rock', "Aphrodite's Rock", 32.627, 34.664], ['castle', 'Kolossi', 32.934, 34.665],
      ['sea', 'Mediterranean Sea', 33.4, 34.35], ['sea', 'Cilician Sea', 33.3, 35.62],
    ],
  },
  Mallorca: {
    geo: [0.769915, 1.80988, -39.96878, 0.86509], sea: '#87b8c2', paper: '#f3e6c9', waterLine: '#1f5868', accent: '#b0402e',
    frame: 'tiles', title: 'Mallorca', serif: true, motif: 'llaut',
    feats: [
      ['town', 'Palma', 2.65, 39.57], ['town', 'Sóller', 2.715, 39.766], ['town', 'Alcúdia', 3.121, 39.853],
      ['town', 'Manacor', 3.21, 39.57], ['town', 'Pollença', 3.016, 39.877], ['town', 'Santanyí', 3.128, 39.354],
      ['peak', 'Puig Major', 2.808, 39.807], ['windmill', 'Pla de Sant Jordi', 2.76, 39.55], ['lighthouse', 'Formentor', 3.211, 39.962],
      ['sea', 'Balearic Sea', 2.35, 39.95], ['sea', 'Mediterranean', 3.05, 39.18],
    ],
  },
  Tasmania: {
    geo: [0.739101, 106.87826, 40.64031, 3.00098], sea: '#93acb8', paper: '#ebe6d0', waterLine: '#2b4757', accent: '#3d5a3a',
    frame: 'rule', title: 'lutruwita', serif: false, motif: 'whale',
    feats: [
      ['town', 'Hobart', 147.325, -42.882], ['town', 'Launceston', 147.137, -41.433], ['town', 'Devonport', 146.35, -41.18],
      ['town', 'Burnie', 145.905, -41.052], ['town', 'Strahan', 145.327, -42.153], ['town', 'St Helens', 148.239, -41.32],
      ['peak', 'Cradle Mountain', 145.952, -41.684], ['peak', 'kunanyi', 147.237, -42.896], ['pines', 'Huon pines', 145.5, -42.3],
      ['sea', 'Bass Strait', 146.6, -40.45], ['sea', 'Tasman Sea', 149.0, -42.4], ['sea', 'Southern Ocean', 145.8, -43.85],
    ],
  },
  'North Island': {
    geo: [0.797622, 137.72871, 34.39764, 7.22266], sea: '#8fb2b8', paper: '#ece8cf', waterLine: '#245059', accent: '#2a2a2a',
    frame: 'koru', title: 'Te Ika-a-Māui', serif: false, motif: 'waka',
    feats: [
      ['town', 'Auckland', 174.763, -36.848], ['town', 'Wellington', 174.776, -41.286], ['town', 'Hamilton', 175.28, -37.787],
      ['town', 'Napier', 176.912, -39.493], ['town', 'Gisborne', 178.018, -38.662], ['town', 'New Plymouth', 174.075, -39.057],
      ['volcano', 'Ruapehu', 175.563, -39.281], ['peak', 'Taranaki', 174.064, -39.296], ['geyser', 'Whakarewarewa', 176.252, -38.163], ['fern', 'Te Urewera', 177.0, -38.7],
      ['sea', 'Tasman Sea', 172.9, -38.2], ['sea', 'Pacific Ocean', 178.4, -40.3], ['sea', 'Cook Strait', 174.4, -41.45],
    ],
  },
  'South Island': {
    geo: [0.723954, 120.51399, 40.50213, 6.17977], sea: '#8eadb7', paper: '#ebe8d3', waterLine: '#244a58', accent: '#2a2a2a',
    frame: 'koru', title: 'Te Waipounamu', serif: false, motif: 'whale',
    feats: [
      ['town', 'Christchurch', 172.636, -43.532], ['town', 'Dunedin', 170.503, -45.878], ['town', 'Nelson', 173.284, -41.271],
      ['town', 'Queenstown', 168.662, -45.031], ['town', 'Invercargill', 168.354, -46.413], ['town', 'Greymouth', 171.21, -42.45],
      ['peak', 'Aoraki', 170.142, -43.595], ['area', 'Piopiotahi', 167.93, -44.64], ['fern', 'Fiordland', 167.6, -45.4], ['whale', 'Kaikōura', 173.85, -42.5],
      ['sea', 'Tasman Sea', 168.8, -42.6], ['sea', 'Pacific Ocean', 173.6, -45.0], ['sea', 'Foveaux Strait', 168.2, -46.75],
    ],
  },
  'Sri Lanka': {
    geo: [0.989862, 78.8888, -9.82958, 3.90584], sea: '#86b5bb', paper: '#f2e5c6', waterLine: '#1f5560', accent: '#8a2a2a',
    frame: 'lotus', title: 'ශ්‍රී ලංකා · இலங்கை', serif: false, motif: 'oruwa',
    feats: [
      ['town', 'Colombo', 79.861, 6.927], ['town', 'Kandy', 80.636, 7.291], ['town', 'Jaffna', 80.008, 9.661],
      ['town', 'Galle', 80.217, 6.053], ['town', 'Trincomalee', 81.233, 8.587], ['town', 'Batticaloa', 81.692, 7.731],
      ['peak', 'Sri Pada', 80.499, 6.809], ['rock', 'Sigiriya', 80.76, 7.957], ['stupa', 'Anuradhapura', 80.396, 8.35],
      ['sea', 'Laccadive Sea', 79.25, 7.2], ['sea', 'Bay of Bengal', 82.0, 8.9], ['sea', 'Indian Ocean', 81.2, 5.6], ['sea', 'Palk Strait', 79.6, 9.95],
    ],
  },
  Madagascar: {
    geo: [0.95786, 41.40151, 11.94362, 13.65495], sea: '#88b5b6', paper: '#f1dfc0', waterLine: '#205759', accent: '#a5432a',
    frame: 'rule', title: 'Madagasikara', serif: false, motif: 'dhow',
    feats: [
      ['town', 'Antananarivo', 47.525, -18.91], ['town', 'Toamasina', 49.402, -18.149], ['town', 'Mahajanga', 46.317, -15.717],
      ['town', 'Toliara', 43.667, -23.35], ['town', 'Antsiranana', 49.292, -12.278], ['town', 'Fianarantsoa', 47.085, -21.453], ['town', 'Morondava', 44.318, -20.285],
      ['baobab', 'Avenue of the Baobabs', 44.418, -20.25], ['ravinala', '', 48.6, -17.0], ['peak', 'Maromokotro', 48.97, -14.02], ['lemur', 'Isalo', 45.4, -22.5],
      ['sea', 'Mozambique Channel', 42.2, -18.4], ['sea', 'Indian Ocean', 51.4, -20.5],
    ],
  },
  Cuba: {
    geo: [0.929485, -78.95943, -23.21239, 10.054], sea: '#80bcbc', paper: '#f3e3c3', waterLine: '#1a5c62', accent: '#2f4f8a',
    frame: 'tiles', title: 'Cuba', serif: true, motif: 'galleon',
    feats: [
      ['town', 'La Habana', -82.366, 23.113], ['town', 'Santiago de Cuba', -75.821, 20.024], ['town', 'Camagüey', -77.917, 21.38],
      ['town', 'Santa Clara', -79.964, 22.406], ['town', 'Pinar del Río', -83.698, 22.417], ['town', 'Holguín', -76.264, 20.887], ['town', 'Cienfuegos', -80.435, 22.146],
      ['lighthouse', 'El Morro', -82.357, 23.15], ['peak', 'Pico Turquino', -76.84, 19.99], ['palm', 'Viñales', -83.711, 22.616], ['palm', '', -78.6, 21.6],
      ['sea', 'Gulf of Mexico', -85.0, 23.4], ['sea', 'Straits of Florida', -80.5, 23.75], ['sea', 'Caribbean Sea', -79.2, 20.6], ['sea', 'Atlantic Ocean', -75.6, 21.6],
    ],
  },
  Taiwan: {
    geo: [0.91519, 109.87074, -25.28742, 3.38281], sea: '#8ab3b9', paper: '#efe6cd', waterLine: '#225560', accent: '#9a2f2a',
    frame: 'cloud', title: '臺灣 · Formosa', serif: false, motif: 'junk',
    feats: [
      ['town', 'Taipei', 121.565, 25.033], ['town', 'Kaohsiung', 120.301, 22.627], ['town', 'Taichung', 120.674, 24.148],
      ['town', 'Tainan', 120.203, 22.997], ['town', 'Hualien', 121.606, 23.976], ['town', 'Taitung', 121.144, 22.756],
      ['skyscraper', 'Taipei 101', 121.565, 25.034], ['peak', 'Yushan', 120.957, 23.47], ['lighthouse', 'Eluanbi', 120.853, 21.902], ['area', 'Taroko', 121.5, 24.18],
      ['sea', 'Taiwan Strait', 119.7, 24.0], ['sea', 'Pacific Ocean', 122.35, 23.2], ['sea', 'Bashi Channel', 120.7, 21.55],
    ],
  },
  Hokkaido: {
    geo: [0.729318, 101.93505, -45.52041, 4.41775], sea: '#9ab3bd', paper: '#edeadf', waterLine: '#2c4c5c', accent: '#9a2f2a',
    frame: 'cloud', title: '北海道', serif: false, motif: 'bezaisen',
    feats: [
      ['town', 'Sapporo', 141.354, 43.062], ['town', 'Hakodate', 140.729, 41.769], ['town', 'Asahikawa', 142.365, 43.771],
      ['town', 'Kushiro', 144.381, 42.985], ['town', 'Obihiro', 143.196, 42.924], ['town', 'Wakkanai', 141.673, 45.415],
      ['volcano', 'Yōtei', 140.811, 42.827], ['peak', 'Asahidake', 142.854, 43.664], ['crane', 'Kushiro Marsh', 144.4, 43.13],
      ['sea', 'Sea of Japan', 140.2, 43.9], ['sea', 'Sea of Okhotsk', 143.9, 44.85], ['sea', 'Pacific Ocean', 143.4, 41.95],
    ],
  },
  'Long Island': {
    geo: [0.75629, -55.99569, -41.16262, 1.65066], sea: '#98b4ba', paper: '#efe7d1', waterLine: '#284e5c', accent: '#2f4f7a',
    frame: 'rope', title: 'Paumanok', serif: true, motif: 'whaler',
    feats: [
      ['town', 'Brooklyn', -73.944, 40.678], ['town', 'Hempstead', -73.619, 40.706], ['town', 'Huntington', -73.426, 40.868],
      ['town', 'Riverhead', -72.662, 40.917], ['town', 'Southampton', -72.39, 40.884], ['town', 'Montauk', -71.954, 41.035], ['town', 'Sag Harbor', -72.293, 41.0],
      ['lighthouse', 'Montauk Point', -71.857, 41.071], ['windmill', 'Hook Mill', -72.185, 40.965],
      ['sea', 'Long Island Sound', -73.0, 41.12], ['sea', 'Atlantic Ocean', -72.9, 40.45],
    ],
  },
  'Isle of Wight': {
    geo: [0.633525, -0.99431, -50.77318, 0.3213], sea: '#9db8b7', paper: '#f1e8d2', waterLine: '#2d5660', accent: '#2f4f7a',
    frame: 'rope', title: 'Vectis', serif: true, motif: 'yacht',
    feats: [
      ['town', 'Newport', -1.293, 50.701], ['town', 'Cowes', -1.298, 50.762], ['town', 'Ryde', -1.164, 50.729],
      ['town', 'Sandown', -1.16, 50.651], ['town', 'Ventnor', -1.205, 50.594], ['town', 'Yarmouth', -1.5, 50.706],
      ['stacks', 'The Needles', -1.586, 50.664], ['castle', 'Carisbrooke', -1.313, 50.687], ['lighthouse', "St Catherine's", -1.297, 50.576],
      ['sea', 'The Solent', -1.38, 50.775], ['sea', 'English Channel', -1.3, 50.535],
    ],
  },
  Honshu: {
    geo: [0.805458, 105.40354, -41.54686, 9.02817], sea: '#90afbb', paper: '#efe7d2', waterLine: '#244a5e', accent: '#b0302a',
    frame: 'cloud', title: '本州', serif: false, motif: 'wave',
    feats: [
      ['town', 'Tokyo', 139.692, 35.69], ['town', 'Osaka', 135.502, 34.694], ['town', 'Kyoto', 135.768, 35.012],
      ['town', 'Nagoya', 136.907, 35.181], ['town', 'Hiroshima', 132.455, 34.385], ['town', 'Sendai', 140.872, 38.268], ['town', 'Aomori', 140.74, 40.822], ['town', 'Niigata', 139.036, 37.916],
      ['peak', 'Fuji', 138.727, 35.361], ['torii', 'Itsukushima', 132.32, 34.296], ['pagoda', 'Tō-ji', 135.747, 34.98],
      ['sea', 'Sea of Japan', 136.4, 38.6], ['sea', 'Pacific Ocean', 141.3, 35.6], ['sea', 'Seto Inland Sea', 133.4, 34.15],
    ],
  },
  Jamaica: {
    geo: [0.950472, -74.49292, -18.52509, 2.07838], sea: '#7fbdb6', paper: '#f3e3c2', waterLine: '#185c58', accent: '#2a7a3a',
    frame: 'rope', title: 'Xaymaca', serif: true, motif: 'galleon',
    feats: [
      ['town', 'Kingston', -76.793, 17.997], ['town', 'Montego Bay', -77.919, 18.471], ['town', 'Spanish Town', -76.957, 17.991],
      ['town', 'Ocho Rios', -77.104, 18.407], ['town', 'Port Antonio', -76.451, 18.176], ['town', 'Negril', -78.348, 18.268], ['town', 'Mandeville', -77.505, 18.042],
      ['peak', 'Blue Mountain Peak', -76.579, 18.046], ['ship', 'Port Royal', -76.841, 17.937], ['palm', '', -77.6, 18.25], ['falls', "Dunn's River", -77.135, 18.413],
      ['sea', 'Caribbean Sea', -77.4, 17.55],
    ],
  },
  Trinidad: {
    geo: [0.983525, -60.90842, -10.84528, 1.00458], sea: '#82b9b3', paper: '#f2e1c1', waterLine: '#1b5a57', accent: '#b0302a',
    frame: 'rope', title: 'Iere', serif: true, motif: 'pirogue',
    feats: [
      ['town', 'Port of Spain', -61.519, 10.661], ['town', 'San Fernando', -61.469, 10.28], ['town', 'Chaguanas', -61.411, 10.517],
      ['town', 'Arima', -61.283, 10.637], ['town', 'Point Fortin', -61.684, 10.174], ['town', 'Sangre Grande', -61.13, 10.587],
      ['steelpan', '', -61.45, 10.62], ['area', 'Pitch Lake', -61.627, 10.231], ['peak', 'El Cerro del Aripo', -61.231, 10.72], ['hummingbird', '', -61.2, 10.4],
      ['sea', 'Gulf of Paria', -61.85, 10.45], ['sea', 'Caribbean Sea', -61.3, 10.95], ['sea', 'Atlantic Ocean', -60.75, 10.3], ['sea', 'Columbus Channel', -61.6, 9.98],
    ],
  },
  Borneo: {
    geo: [0.999587, 108.81053, -7.02676, 11.21094], sea: '#84b3b0', paper: '#ece2c3', waterLine: '#1e5452', accent: '#2f6b3a',
    frame: 'lotus', title: 'Pulau Borneo', serif: false, motif: 'perahu',
    feats: [
      ['town', 'Kota Kinabalu', 116.073, 5.98], ['town', 'Kuching', 110.344, 1.553], ['town', 'Pontianak', 109.333, -0.026],
      ['town', 'Banjarmasin', 114.59, -3.317], ['town', 'Balikpapan', 116.83, -1.267], ['town', 'Bandar Seri Begawan', 114.94, 4.89], ['town', 'Samarinda', 117.15, -0.5],
      ['peak', 'Kinabalu', 116.558, 6.075], ['jungle', '', 113.5, 0.8], ['jungle', '', 116.0, 2.5], ['area', 'Kapuas', 111.6, 0.1], ['longhouse', '', 112.6, 2.1],
      ['sea', 'South China Sea', 112.4, 5.3], ['sea', 'Sulu Sea', 118.8, 6.6], ['sea', 'Celebes Sea', 119.6, 3.0], ['sea', 'Java Sea', 112.0, -4.7], ['sea', 'Makassar Strait', 118.3, -1.6],
    ],
  },
  Newfoundland: {
    geo: [0.660693, -39.24904, -51.62775, 5.0094], sea: '#9cb0b9', paper: '#ebe8de', waterLine: '#2c4756', accent: '#2f4f7a',
    frame: 'rope', title: 'Ktaqmkuk', serif: true, motif: 'whale',
    feats: [
      ['town', "St. John's", -52.712, 47.561], ['town', 'Corner Brook', -57.952, 48.951], ['town', 'Gander', -54.608, 48.956],
      ['town', 'Grand Falls-Windsor', -55.65, 48.933], ['town', 'Stephenville', -58.55, 48.55], ['town', 'St. Anthony', -55.59, 51.36],
      ['lighthouse', 'Cape Spear', -52.619, 47.524], ['peak', 'Gros Morne', -57.787, 49.595], ['longhouse', "L'Anse aux Meadows", -55.534, 51.596], ['iceberg', 'Iceberg Alley', -52.4, 49.6],
      ['sea', 'Atlantic Ocean', -52.9, 46.4], ['sea', 'Gulf of St. Lawrence', -59.6, 48.0], ['sea', 'Strait of Belle Isle', -56.6, 51.75],
    ],
  },
};
